import React, { useState } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useLocalization, type Language } from '../../../config/localization';

interface HeaderOrganizationSelectorProps {
  selectedLanguage?: Language;
}

const HeaderOrganizationSelector: React.FC<HeaderOrganizationSelectorProps> = ({ selectedLanguage = 'ru' }) => {
  const t = useLocalization(selectedLanguage);
  const {
    userAccess,
    currentOrganization,
    switchOrganization
  } = useOrganization();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  if (!userAccess || !userAccess.organizations || userAccess.organizations.length === 0) {
    return null;
  }

  const handleSwitchOrganization = async (orgId: number) => {
    if (isSwitching || orgId === currentOrganization?.id) return;

    setIsSwitching(true);
    try {
      await switchOrganization(orgId);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to switch organization:', error);
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div 
      className="relative"
      data-testid="header-organization-selector"
      data-language={selectedLanguage}
    >
      {/* Current Organization Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 transition-colors"
        disabled={isSwitching}
      >
        <div className="flex items-center space-x-2 flex-1">
          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
          <span className="text-white font-medium truncate">
            {currentOrganization?.name || t.orgSelector?.selectOrganization || 'Select Organization'}
          </span>
        </div>
        
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform ml-2 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown - Only organization switching */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50">
          <div className="p-2">
            <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-700">
              {t.orgSelector?.availableOrganizations || 'Available Organizations'}
            </div>
            
            <div className="max-h-60 overflow-y-auto">
              {(userAccess?.organizations || []).map((org) => {
                const isSelected = org.id === currentOrganization?.id;

                return (
                  <button
                    key={org.id}
                    onClick={() => handleSwitchOrganization(org.id)}
                    disabled={isSwitching || isSelected}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      isSelected
                        ? 'bg-purple-500/20 border border-purple-500/50'
                        : 'hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        isSelected ? 'bg-purple-500' : 'bg-gray-600'
                      }`}></div>

                      <div className="text-sm font-medium text-white">
                        {org.name}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* Loading Overlay */}
      {isSwitching && (
        <div className="absolute inset-0 bg-gray-900/50 rounded-lg flex items-center justify-center">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent"></div>
        </div>
      )}
      
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default HeaderOrganizationSelector;